import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Crown, Sparkles, Heart, Zap, Star, ArrowRight } from 'lucide-react';

const UpgradeModal = ({ isOpen, onClose, feature, usageStats }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getFeatureDetails = (feature) => {
    const featureMap = {
      ai_icebreakers: {
        title: "AI-Powered Icebreakers",
        description: "Get unlimited personalized icebreakers that spark meaningful connections",
        icon: <Sparkles className="w-8 h-8" />,
        benefit: "3x more engaging conversations"
      },
      ai_profile_suggestions: {
        title: "Smart Profile Optimization",
        description: "Get AI recommendations to make your profile irresistible to potential matches",
        icon: <Star className="w-8 h-8" />,
        benefit: "5x more profile views"
      },
      ai_compatibility_analysis: {
        title: "Compatibility Analysis",
        description: "Deep AI insights into your compatibility with potential matches",
        icon: <Heart className="w-8 h-8" />,
        benefit: "Find your perfect match faster"
      },
      ai_smart_matching: {
        title: "Smart Matching",
        description: "AI ranks your best potential matches based on deep compatibility analysis",
        icon: <Zap className="w-8 h-8" />,
        benefit: "2x higher match success rate"
      },
      ai_conversation_coaching: {
        title: "Advanced AI Assistant",
        description: "Get smart conversation suggestions during your speed dating sessions",
        icon: <Crown className="w-8 h-8" />,
        benefit: "Boost confidence & conversation flow"
      },
      session_complete: {
        title: "Session Complete!",
        description: "Ready for unlimited speed dating sessions? Upgrade to Premium for unlimited sessions, advanced matching, and AI assistance!",
        icon: <Heart className="w-8 h-8" />,
        benefit: "Unlimited sessions + Premium features"
      },
      chat: {
        title: "Chat with Your Matches",
        description: "Upgrade to Premium or VIP to unlock unlimited messaging with all your matches!",
        icon: <Heart className="w-8 h-8" />,
        benefit: "Connect and build relationships"
      }
    };

    return featureMap[feature] || {
      title: "Premium Features",
      description: "Unlock all AI-powered dating tools",
      icon: <Crown className="w-8 h-8" />,
      benefit: "Complete dating advantage"
    };
  };

  const featureDetails = getFeatureDetails(feature);

  const handleUpgrade = () => {
    onClose();
    navigate('/subscription/plans');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="max-w-lg w-full max-h-[95vh] overflow-y-auto">
        <Card className="w-full bg-white/5 backdrop-blur-xl border border-white/20 shadow-2xl animate-bounceIn">
          <CardHeader className="relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                {featureDetails.icon}
              </div>
              
              <CardTitle className="text-2xl font-bold text-white mb-2">
                Unlock {featureDetails.title}
              </CardTitle>
            
            <p className="text-gray-300 text-base">
              {featureDetails.description}
            </p>
          </div>
        </CardHeader>

        <CardContent className="pb-6">
          {/* Usage Stats */}
          {usageStats && usageStats.used !== undefined && usageStats.limit !== undefined && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-amber-400 font-semibold">Free Tier Limit Reached</h4>
                  <p className="text-amber-200 text-sm">
                    You've used {usageStats.used} of {usageStats.limit} free uses this month
                  </p>
                </div>
                <div className="text-amber-400 text-2xl">
                  {usageStats.limit > 0 ? Math.round((usageStats.used / usageStats.limit) * 100) : 0}%
                </div>
              </div>
            </div>
          )}

          {/* Feature Benefit */}
          <div className="bg-gradient-to-r from-rose-500/10 to-pink-500/10 border border-rose-500/20 rounded-xl p-4 mb-6">
            <div className="text-center">
              <h3 className="text-rose-400 font-semibold text-base mb-2">
                🚀 {featureDetails.benefit}
              </h3>
              <p className="text-gray-300 text-sm">
                Join thousands of successful daters who upgraded to Premium
              </p>
            </div>
          </div>

          {/* Premium Benefits */}
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-4 flex items-center">
              <Crown className="w-5 h-5 text-rose-400 mr-2" />
              What you get with Premium:
            </h4>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Unlimited AI features</span>
              </div>
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Smart compatibility analysis</span>
              </div>
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Priority matching algorithm</span>
              </div>
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Real-time coaching tips</span>
              </div>
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Unlimited speed dating</span>
              </div>
              <div className="flex items-center text-gray-300">
                <div className="w-2 h-2 bg-rose-400 rounded-full mr-3"></div>
                <span>Advanced analytics</span>
              </div>
            </div>
          </div>

          {/* Pricing Highlight */}
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                $9.99<span className="text-base text-gray-400">/month</span>
              </div>
              <p className="text-purple-400 font-medium text-sm">Save 17% with yearly plan</p>
              <p className="text-gray-300 text-xs mt-1">Cancel anytime • 7-day money-back guarantee</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleUpgrade}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-3 text-base font-semibold shadow-lg hover:shadow-rose-500/25 transform hover:scale-105 transition-all duration-300"
            >
              Upgrade to Premium
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/10 py-2"
            >
              Maybe Later
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex items-center justify-center space-x-8 mt-6 pt-4 border-t border-white/10">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">50K+</div>
              <div className="text-gray-400 text-sm">Happy Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">4.9★</div>
              <div className="text-gray-400 text-sm">App Rating</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">2M+</div>
              <div className="text-gray-400 text-sm">Matches Made</div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default UpgradeModal;